import { useState } from 'react';
import { motion } from 'framer-motion';
import { PlayCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import 'photoswipe/dist/photoswipe.css';
import { Gallery, Item } from 'react-photoswipe-gallery';

interface ImageGalleryProps {
  images: string[];
  title: string;
  videoUrl?: string;
}

export default function ImageGallery({ images, title, videoUrl }: ImageGalleryProps) {
  const [showVideo, setShowVideo] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const handleThumbnailClick = (index: number) => {
    setSelectedImageIndex(index);
  };

  return (
    <>
      <div className="mb-8">
        <Gallery>
          {/* Main featured image */}
          <div className="mb-4">
            <Item
              original={images[selectedImageIndex]}
              thumbnail={images[selectedImageIndex]}
              width="1600"
              height="1200"
            >
              {({ ref, open }) => (
                <motion.div
                  ref={ref as any}
                  onClick={open}
                  className="cursor-pointer aspect-[16/10] overflow-hidden rounded-lg"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <img
                    src={images[selectedImageIndex]}
                    alt={`${title} - Imagem principal`}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </motion.div>
              )}
            </Item>
          </div>

          {/* Thumbnail grid - showing all images including the main one */}
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {images.map((image, index) => (
              <div key={index} className="relative">
                <Item
                  original={image}
                  thumbnail={image}
                  width="1600"
                  height="1200"
                >
                  {({ ref, open }) => (
                    <motion.div
                      ref={ref as any}
                      className={`cursor-pointer aspect-[4/3] overflow-hidden rounded-lg border-2 transition-all ${
                        selectedImageIndex === index 
                          ? 'border-primary ring-2 ring-primary/20' 
                          : 'border-transparent hover:border-primary/50'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleThumbnailClick(index);
                      }}
                      onDoubleClick={open}
                    >
                      <img
                        src={image}
                        alt={`${title} - Imagem ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </motion.div>
                  )}
                </Item>
              </div>
            ))}
            
            {/* Video thumbnail */}
            {videoUrl && (
              <div className="relative">
                <div 
                  className="cursor-pointer aspect-[4/3] overflow-hidden rounded-lg border-2 border-transparent hover:border-primary/50 transition-all relative group"
                  onClick={() => setShowVideo(true)}
                >
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center group-hover:bg-black/60 transition-colors">
                    <PlayCircle className="h-6 w-6 text-white opacity-80 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <img
                    src={images[0]}
                    alt="Video thumbnail"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        </Gallery>
      </div>

      {/* Video Modal */}
      {showVideo && videoUrl && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-5xl aspect-video bg-black rounded-lg overflow-hidden">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 z-10 text-white hover:text-white/80"
              onClick={() => setShowVideo(false)}
            >
              <X className="h-6 w-6" />
            </Button>
            <iframe
              src={videoUrl}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        </div>
      )}
    </>
  );
}